import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { ArrowRight, ShieldCheck, RotateCcw } from 'lucide-react';

const ARIAONE_LOGO = "/ariaone_logo_black.png";

const MFAPage = () => {
  const navigate = useNavigate();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(30);
  const refs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    refs[0].current?.focus();
    const timer = setInterval(() => setCountdown(c => c > 0 ? c - 1 : 0), 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDigit = (idx, val) => {
    if (!/^[0-9]?$/.test(val)) return;
    const next = [...digits];
    next[idx] = val;
    setDigits(next);
    if (val && idx < 5) refs[idx + 1].current?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      refs[idx - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length > 0) {
      const next = [...digits];
      text.split('').forEach((d, i) => { next[i] = d; });
      setDigits(next);
      refs[Math.min(text.length, 5)].current?.focus();
      e.preventDefault();
    }
  };

  const handleVerify = (e) => {
    e.preventDefault();
    navigate('/app-selector');
  };

  const code = digits.join('');
  const isComplete = code.length === 6;

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-4" data-testid="mfa-page">
      <div className="w-full max-w-[420px] space-y-8">
        {/* Logo */}
        <div className="text-center">
          <img src={ARIAONE_LOGO} alt="AriaOne" className="h-8 mx-auto mb-2" data-testid="mfa-logo" />
          <p className="text-xs text-slate-400 tracking-wider font-medium">HEALTHCARE PLATFORM</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="h-6 w-6 text-slate-700" />
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-1" data-testid="mfa-title">Verify your identity</h1>
          <p className="text-sm text-slate-500 mb-6">Enter the 6-digit code sent to your email</p>

          <form onSubmit={handleVerify} className="space-y-6">
            <div className="flex justify-center gap-3" data-testid="mfa-code-inputs">
              {digits.map((d, i) => (
                <Input key={i} ref={refs[i]} maxLength={1} value={d}
                  onChange={e => handleDigit(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  onPaste={i === 0 ? handlePaste : undefined}
                  className={cn("w-12 h-14 text-center text-xl font-bold border-slate-200 rounded-xl focus-visible:ring-slate-500 focus-visible:border-slate-500",
                    d ? "border-slate-400 bg-slate-50" : "")}
                  data-testid={`mfa-digit-${i}`} />
              ))}
            </div>

            {/* Demo disclaimer */}
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="mfa-demo-disclaimer">
              <ShieldCheck className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
              <div className="text-left">
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  <span className="font-semibold text-slate-700">Demo mode.</span> This is a simulated verification. Use the temporary code below:
                </p>
                <p className="text-lg font-mono font-bold text-slate-900 tracking-[0.25em] mt-1" data-testid="mfa-temp-code">1 2 3 4 5 6</p>
              </div>
            </div>

            <Button type="submit" disabled={!isComplete}
              className={cn("w-full h-11 text-sm font-medium transition-all",
                isComplete ? "bg-slate-900 hover:bg-slate-800" : "bg-slate-200 text-slate-400 cursor-not-allowed")}
              data-testid="mfa-verify-btn">
              Verify & continue <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </form>

          <div className="mt-4 flex items-center justify-center gap-2">
            {countdown > 0 ? (
              <p className="text-xs text-slate-400">Resend code in <span className="font-mono font-semibold text-slate-600">{countdown}s</span></p>
            ) : (
              <button onClick={() => setCountdown(30)} className="text-xs text-slate-700 hover:underline font-medium flex items-center gap-1" data-testid="mfa-resend">
                <RotateCcw className="h-3 w-3" /> Resend code
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-400">Check your spam folder if you don't see the email.</p>
      </div>
    </div>
  );
};

export default MFAPage;
